import moment from 'moment';
import Utils from '../../services/Utils';

export const DATE_RANGE_PRESET_IDS = [
    'now',
    'next7',
    'next30',
    'last7',
    'last30',
    'tomorrow',
    'next_week',
    'next_cinema_week',
    'next_month',
    'next_year',
    'today',
    'yesterday',
    'this_week',
    'this_cinema_week',
    'this_month',
    'this_year',
    'last_week',
    'last_cinema_week',
    'last_month',
    'last_year',
    'custom',
];

export function isCinemaCustomerType(setting) {
    const t = setting?.customer?.type;
    return t === Utils.CUSTOMER_TYPE_CINEMA || t === Utils.CUSTOMER_TYPE_CINEMA_FESTIVAL;
}

/** Stable ISO for filter equality (ignore sub-minute drift / EOD ms). */
export function filter_date_to_minute_iso(raw) {
    if (raw == null || raw === '')
        return null;
    if (moment.isMoment(raw))
        return raw.clone().startOf('minute').toISOString();
    const m = moment(raw);
    return m.isValid() ? m.clone().startOf('minute').toISOString() : String(raw);
}

/**
 * Cinema week presets: cinema / cinema-festival instances, or any instance with at least one own movie.
 * @param {boolean | null | undefined} hasOwnMovies Resolved at app load (`true`/`false`); `null` while loading.
 */
export function allowsCinemaWeekPresets(setting, hasOwnMovies) {
    if (isCinemaCustomerType(setting))
        return true;
    return hasOwnMovies === true;
}

export const cinemaWeekDatePresetIds = [
    'next_cinema_week',
    'this_cinema_week',
    'last_cinema_week',
];

/**
 * @returns {{ start: import('moment').Moment, end: import('moment').Moment | null } | null}
 */
export function resolve_date_range_preset(preset, now = null) {
    const n = (moment.isMoment(now) ? now : moment(now || undefined)).clone();
    switch (preset) {
        case 'today':
            return { start: n.clone().startOf('day'), end: n.clone().endOf('day') };
        case 'now':
            return { start: n.clone(), end: null };
        case 'next7':
            return { start: n.clone().startOf('day'), end: n.clone().add(6, 'days').endOf('day') };
        case 'next30':
            return { start: n.clone().startOf('day'), end: n.clone().add(29, 'days').endOf('day') };
        case 'tomorrow': {
            const d = n.clone().add(1, 'day');
            return { start: d.clone().startOf('day'), end: d.clone().endOf('day') };
        }
        case 'next_week': {
            const s = n.clone().add(1, 'week').startOf('isoWeek');
            return { start: s, end: s.clone().endOf('isoWeek') };
        }
        case 'next_cinema_week': {
            const start = n.clone();
            const iso = start.isoWeekday();
            const delta = iso >= 3 ? iso - 3 : iso + 4;
            start.subtract(delta, 'days').startOf('day').add(7, 'days');
            return { start, end: start.clone().add(6, 'days').endOf('day') };
        }
        case 'next_month': {
            const s = n.clone().add(1, 'month').startOf('month');
            return { start: s, end: s.clone().endOf('month') };
        }
        case 'next_year': {
            const s = n.clone().add(1, 'year').startOf('year');
            return { start: s, end: s.clone().endOf('year') };
        }
        case 'yesterday': {
            const y = n.clone().subtract(1, 'day');
            return { start: y.clone().startOf('day'), end: y.clone().endOf('day') };
        }
        case 'this_week':
            return { start: n.clone().startOf('isoWeek'), end: n.clone().endOf('isoWeek') };
        case 'this_cinema_week': {
            // Cinema week starts on Wednesday (isoWeekday 3).
            const start = n.clone();
            const iso = start.isoWeekday();
            const delta = iso >= 3 ? iso - 3 : iso + 4;
            start.subtract(delta, 'days').startOf('day');
            return { start, end: start.clone().add(6, 'days').endOf('day') };
        }
        case 'last7':
            return { start: n.clone().subtract(6, 'days').startOf('day'), end: n.clone().endOf('day') };
        case 'last30':
            return { start: n.clone().subtract(29, 'days').startOf('day'), end: n.clone().endOf('day') };
        case 'this_month':
            return { start: n.clone().startOf('month'), end: n.clone().endOf('month') };
        case 'this_year':
            return { start: n.clone().startOf('year'), end: n.clone().endOf('year') };
        case 'last_week':
            return {
                start: n.clone().subtract(1, 'week').startOf('isoWeek'),
                end: n.clone().subtract(1, 'week').endOf('isoWeek'),
            };
        case 'last_cinema_week': {
            const start = n.clone();
            const iso = start.isoWeekday();
            const delta = iso >= 3 ? iso - 3 : iso + 4;
            start.subtract(delta, 'days').startOf('day').subtract(7, 'days');
            return { start, end: start.clone().add(6, 'days').endOf('day') };
        }
        case 'last_month': {
            const s = n.clone().subtract(1, 'month').startOf('month');
            return { start: s, end: s.clone().endOf('month') };
        }
        case 'last_year': {
            const s = n.clone().subtract(1, 'year').startOf('year');
            return { start: s, end: s.clone().endOf('year') };
        }
        default:
            return null;
    }
}

/** Latest instant among raw date values, snapped to end of that calendar day. */
export function max_date_end_of_day_iso(raw_dates, now = null) {
    let max = null;
    for (const raw of raw_dates || []) {
        const m = moment(raw);
        if (m.isValid() && (!max || m.isAfter(max)))
            max = m;
    }
    if (max)
        return max.clone().endOf('day').toISOString();
    const n = moment.isMoment(now) ? now.clone() : moment(now || undefined);
    return n.endOf('day').toISOString();
}

/** Like `max_date_end_of_day_iso` but returns null when the input set is empty. */
export function latest_date_end_of_day_iso(raw_dates) {
    let max = null;
    for (const raw of raw_dates || []) {
        const m = moment(raw);
        if (m.isValid() && (!max || m.isAfter(max)))
            max = m;
    }
    return max ? max.clone().endOf('day').toISOString() : null;
}

/**
 * Custom range with no end bound, same start as persisted analysis — equivalent
 * to an open-ended preset (`now`) for auto-apply deduplication.
 */
export function open_custom_range_matches_analysis(start, end, analysis) {
    if (!analysis || analysis.date_to != null)
        return false;
    if (end != null && end !== '')
        return false;
    const start_m = moment(start);
    const from_m = moment(analysis.date_from);
    return start_m.isValid() && from_m.isValid() && start_m.isSame(from_m, 'minute');
}

/**
 * ISO bounds for seeding redux-form when switching from a preset to custom.
 * Open-ended presets (`now`) use `open_end_fallback` (e.g. max date in loaded data).
 */
export function custom_seed_from_preset(preset, now = null, { open_end_fallback = null } = {}) {
    const r = resolve_date_range_preset(preset, now);
    if (!r?.start)
        return null;
    const end_iso = r.end
        ? r.end.toISOString()
        : max_date_end_of_day_iso(open_end_fallback ? [open_end_fallback] : [], now);
    return {
        start: r.start.toISOString(),
        end: end_iso,
    };
}

/** i18n key suffix under `statstypes.form` (today uses `_short` like DateRangePresetControl). */
export function date_range_preset_label(preset_id, t) {
    if (preset_id === 'now')
        return t('statstypes.form.date_preset_now_day_granularity');
    if (preset_id === 'today')
        return t('statstypes.form.date_preset_today_short');
    return t(`statstypes.form.date_preset_${preset_id}`);
}
